import { useCallback, useEffect, useState } from "react";
import { setCookie, deleteCookie } from "cookies-next";
import {
  COOKIES_USE_GLCC_NATIVE_TOKEN,
  COOKIES_GLCC_NATIVE_ACCESS_TOKEN,
} from "@/constants/cookies";

import { useApiClient } from "@/context/api-client";
import { useLocalStorage } from "@/context/local-storage";

export const unImpersonate = () => {
  deleteCookie(COOKIES_GLCC_NATIVE_ACCESS_TOKEN);
  setCookie(COOKIES_USE_GLCC_NATIVE_TOKEN, false);
};

/**
 * NOTE
 *
 * How to impersonate?
 * https://www.notion.so/goglencoco/How-to-impersonate-a-user-customer-fc403648d9d841dda7281d3333e9d74a?pvs=4
 */
export const useImpersonate = () => {
  const apiClient = useApiClient();
  const storage = useLocalStorage();
  const [isAuthorized, setAuthorized] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const clearListRelatedLocalStorageItems = useCallback(() => {
    /**
     * Reset active list id to avoid requesting list which is not assigned
     * to the impersonated user
     */
    storage.activeListId = null;
    storage.resetAllListFilters();
  }, []);

  useEffect(() => {
    unImpersonate();

    const checkAccess = async () => {
      try {
        const resp = await apiClient.checkAdminAccess();

        if (resp.status === 200) {
          clearListRelatedLocalStorageItems();
          setAuthorized(true);
        } else {
          setAuthorized(false);
        }
      } catch {
        setAuthorized(false);
      }
    };

    checkAccess();
  }, []);

  // Function to handle button click
  const impersonate = async (email: string) => {
    try {
      setIsImpersonating(true);

      const accessToken = await apiClient.getGlencocoToken(email);

      // - Set local storage that this is in 'impersonate' mode
      //   which turns off auth0 AuthLocalProvider setups

      // - Or use a different cookie path & set flag saying APIs should use
      //   this token instead of the one from Auth0

      if (accessToken) {
        setCookie(COOKIES_GLCC_NATIVE_ACCESS_TOKEN, accessToken.data.token);
        setCookie(COOKIES_USE_GLCC_NATIVE_TOKEN, true);
        clearListRelatedLocalStorageItems();

        return true;
      } else {
        unImpersonate();

        return false;
      }
    } catch (e) {
      console.warn(`Admin failed to acquire glencoco access token`, {
        error: e,
      });

      unImpersonate();

      return false;
    } finally {
      setIsImpersonating(false);
    }
  };

  return { isAuthorized, isImpersonating, impersonate };
};
